import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Form, Input, Button, Typography, message, Alert, Modal } from 'antd';
import { UserOutlined, LockOutlined, WhatsAppOutlined, ThunderboltOutlined, EnvironmentOutlined, MailOutlined, StarOutlined, TrophyOutlined, CheckCircleOutlined } from '@ant-design/icons';
import api, { PLAYER_ACTIVE_TEAM_KEY } from '../../services/api';
import useGoogleIdentity from '../../hooks/useGoogleIdentity';

const { Title, Text, Paragraph } = Typography;

const BRAND = '#25D366';
const BRAND_DARK = '#128C7E';
const BRAND_DARKER = '#075E54';
const INK = '#0b1f1a';

const GRID_BG =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'><path d='M60 0H0v60' fill='none' stroke='rgba(255,255,255,0.08)' stroke-width='1'/></svg>\")";

const FEATURES = [
  { icon: <EnvironmentOutlined />, text: 'Mapa da tua região' },
  { icon: <MailOutlined />, text: 'Convite direto no celular' },
  { icon: <StarOutlined />, text: 'Reputação real' },
  { icon: <TrophyOutlined />, text: 'MVP e estatísticas' },
];

const PlayerLogin = () => {
  const [loading, setLoading] = useState(false);
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [regModal, setRegModal] = useState({ open: false, idToken: '', profile: null });
  const [regLoading, setRegLoading] = useState(false);
  const [regForm] = Form.useForm();
  const [regStep, setRegStep] = useState(1); // 1: dados; 2: código
  const [phoneOtp, setPhoneOtp] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [otpCooldownMs, setOtpCooldownMs] = useState(0);
  const [sending, setSending] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const gBtnRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const finishLogin = (token, player) => {
    localStorage.setItem('community_token', token);
    localStorage.setItem('community_user', JSON.stringify(player));
    localStorage.removeItem(PLAYER_ACTIVE_TEAM_KEY);
    message.success(`Bem-vindo${player.name ? `, ${player.name.split(' ')[0]}` : ''}!`);
    // Respeita ?next=... quando veio de um fluxo especifico (ex: /time/setup
    // pro dono de time). Sem next, cai no dashboard do jogador.
    const params = new URLSearchParams(window.location.search);
    const next = params.get('next');
    const dest = next && next.startsWith('/') ? next : '/player/dashboard';
    setTimeout(() => { window.location.href = dest; }, 400);
  };

  const onGoogleCredential = useCallback(async (idToken) => {
    setLoading(true);
    try {
      const r = await api.post('/community/auth/google/verify', { id_token: idToken });
      if (r.data.logged_in) {
        finishLogin(r.data.token, r.data.player);
      } else {
        setRegModal({ open: true, idToken, profile: r.data.google_profile });
        setRegStep(1);
        setPhoneOtp('');
        setCodeInput('');
        setOtpCooldownMs(0);
        regForm.setFieldsValue({ name: r.data.google_profile?.name || '', phone: '' });
      }
    } catch (err) {
      const data = err?.response?.data;
      if (data?.error === 'conta_ja_tem_outro_google_linkado') {
        Modal.error({ title: 'Conta com outro Google', content: data.mensagem });
      } else {
        message.error(data?.error || 'Erro ao autenticar com Google');
      }
    }
    setLoading(false);
  }, [regForm]);

  // Cooldown de reenvio (countdown local)
  useEffect(() => {
    if (otpCooldownMs <= 0) return;
    const t = setInterval(() => setOtpCooldownMs(prev => Math.max(0, prev - 1000)), 1000);
    return () => clearInterval(t);
  }, [otpCooldownMs]);

  const humanCooldown = (ms) => {
    if (ms <= 0) return '';
    const s = Math.ceil(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.ceil(s / 60);
    if (m < 60) return `${m}min`;
    const h = Math.ceil(m / 60);
    return `${h}h`;
  };

  const sendOtp = async () => {
    const phoneVal = regForm.getFieldValue('phone');
    const nameVal = regForm.getFieldValue('name');
    if (!nameVal || !phoneVal || !/^\d{10,11}$/.test(phoneVal)) {
      message.warning('Preencha nome e WhatsApp válido');
      return;
    }
    setSending(true);
    try {
      // purpose=link_google: se o phone ja tem cadastro, o backend nao
      // rejeita (permite mandar OTP pra vincular esse Google novo a conta
      // existente). Aviso pro usuario aparece so quando /google/register
      // efetivamente cria/vincula/rejeita.
      const r = await api.post('/community/auth/signup-otp/request', {
        phone: phoneVal, purpose: 'link_google',
      });
      setPhoneOtp(phoneVal);
      setOtpCooldownMs(r.data?.next_resend_in_ms || 0);
      setRegStep(2);
      message.success('Código enviado no seu WhatsApp');
    } catch (err) {
      const data = err?.response?.data;
      if (data?.error === 'cooldown') {
        setPhoneOtp(phoneVal);
        setOtpCooldownMs(data.retry_in_ms || 0);
        setRegStep(2);
        message.info(data.mensagem);
      } else if (data?.error === 'sem_time_ativo') {
        Modal.error({ title: 'Sem time ativo', content: data.mensagem });
      } else {
        message.error(data?.mensagem || data?.error || 'Erro ao enviar código');
      }
    }
    setSending(false);
  };

  useGoogleIdentity({ containerRef: gBtnRef, onCredential: onGoogleCredential });

  const submitRegister = async () => {
    const name = regForm.getFieldValue('name');
    if (!name || !phoneOtp || !codeInput || codeInput.length < 6) {
      message.warning('Digite o código completo');
      return;
    }
    setRegLoading(true);
    try {
      // 1) Verifica OTP
      await api.post('/community/auth/signup-otp/verify', { phone: phoneOtp, code: codeInput });

      // 2) Cria conta Google ou linka Google a conta existente pelo phone
      const r = await api.post('/community/auth/google/register', {
        id_token: regModal.idToken,
        name,
        phone: phoneOtp,
      });
      if (r.data.linked_existing_account) {
        message.success('Google vinculado à sua conta do WhatsApp. Bem-vindo de volta!');
      }
      finishLogin(r.data.token, r.data.player);
    } catch (err) {
      const data = err?.response?.data;
      const code = data?.error;
      if (code === 'codigo_incorreto' || code === 'codigo_expirado' || code === 'sem_codigo' || code === 'muitas_tentativas') {
        message.error(data.mensagem);
      } else if (code === 'sem_time_ativo') {
        Modal.error({ title: 'Sem time ativo', content: data.mensagem });
      } else if (code === 'phone_ja_vinculado_a_outra_conta_google') {
        Modal.error({ title: 'WhatsApp já em outra conta Google', content: data.mensagem });
      } else if (code === 'email_ja_cadastrado_com_outro_telefone') {
        Modal.error({ title: 'E-mail já cadastrado', content: data.mensagem });
      } else if (code === 'email_vinculado_a_outra_conta_google') {
        Modal.error({ title: 'E-mail em outra conta Google', content: data.mensagem });
      } else {
        message.error(data?.mensagem || code || 'Erro no cadastro');
      }
    }
    setRegLoading(false);
  };

  const handlePasswordLogin = async (values) => {
    setLoading(true);
    try {
      const r = await api.post('/community/players/login', values);
      if (r.data.token) finishLogin(r.data.token, r.data.player);
    } catch (err) {
      message.error(err.response?.data?.message || 'Erro no login');
    }
    setLoading(false);
  };

  const brandBadge = (size = 44) => (
    <img src="/pwa/icon-192.png" alt="Futezap" width={size} height={size}
      style={{ borderRadius: 14, boxShadow: '0 8px 20px rgba(0,0,0,0.15)' }} />
  );

  const leftPane = (
    <div style={{
      position: 'relative',
      background: `radial-gradient(ellipse at top left, ${BRAND_DARK}, transparent 50%),
                   radial-gradient(ellipse at top right, ${BRAND}, transparent 50%),
                   linear-gradient(180deg, ${BRAND_DARKER} 0%, #041a15 100%)`,
      color: '#fff',
      padding: '48px 56px',
      display: 'flex', flexDirection: 'column',
      minHeight: '100vh',
      overflow: 'hidden',
    }}>
      {/* Orbs de blur decorativos (mesmo padrao da LP) */}
      <div style={{
        position: 'absolute', top: -100, right: -100, width: 400, height: 400,
        borderRadius: '50%', background: BRAND, opacity: 0.25, filter: 'blur(80px)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: -120, left: -120, width: 500, height: 500,
        borderRadius: '50%', background: BRAND_DARK, opacity: 0.3, filter: 'blur(100px)',
        pointerEvents: 'none',
      }} />
      {/* Grid pattern sutil */}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: GRID_BG, opacity: 0.15, pointerEvents: 'none' }} />

      {/* Logo/brand no topo */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12 }}>
        <img src="/pwa/icon-192.png" alt="Futezap" width={44} height={44}
          style={{ borderRadius: 12, boxShadow: '0 10px 24px rgba(0,0,0,0.35)' }} />
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: -0.3 }}>Futezap</div>
          <div style={{ fontSize: 10, opacity: 0.7, letterSpacing: 1.5, fontWeight: 600 }}>COMUNIDADE</div>
        </div>
      </div>

      <div style={{ position: 'relative', marginTop: 'auto' }}>
        {/* Badge topo (mesmo estilo da LP) */}
        <div style={{
          display: 'inline-block', padding: '6px 14px', borderRadius: 100,
          background: 'rgba(37, 211, 102, 0.15)', border: '1px solid rgba(37, 211, 102, 0.4)',
          color: BRAND, fontSize: 12, fontWeight: 700, marginBottom: 20,
          letterSpacing: 0.3,
        }}>
          <ThunderboltOutlined /> A REDE DA PELADA
        </div>

        <h1 style={{
          fontSize: 'clamp(38px, 5vw, 56px)',
          fontWeight: 900, lineHeight: 1.05, letterSpacing: -1.5,
          margin: 0, maxWidth: 560, color: '#fff',
        }}>
          Onde boleiros e times<br/>
          <span style={{
            background: `linear-gradient(90deg, ${BRAND}, #a3e635)`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>se encontram!</span>
        </h1>
        <p style={{
          marginTop: 20, fontSize: 17, lineHeight: 1.6, color: 'rgba(255,255,255,0.8)',
          maxWidth: 480,
        }}>
          Time e boleiro da tua região no mapa. Convite direto no celular,
          reputação transparente. De graça.
        </p>

        {/* Features chips */}
        <div style={{ marginTop: 28, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {FEATURES.map(f => (
            <div key={f.text} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 14px', borderRadius: 100,
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.15)',
              fontSize: 13, fontWeight: 500, backdropFilter: 'blur(6px)',
            }}>
              <span style={{ color: BRAND, fontSize: 14 }}>{f.icon}</span> {f.text}
            </div>
          ))}
        </div>

        {/* Sinais de confianca */}
        <div style={{ marginTop: 28, display: 'flex', gap: 20, flexWrap: 'wrap', color: 'rgba(255,255,255,0.75)', fontSize: 13 }}>
          <span><CheckCircleOutlined style={{ color: BRAND }} /> 100% grátis</span>
          <span><CheckCircleOutlined style={{ color: BRAND }} /> Cadastro em 2 min</span>
          <span><CheckCircleOutlined style={{ color: BRAND }} /> Login com Google</span>
        </div>
      </div>
    </div>
  );

  const rightPane = (
    <div style={{
      background: '#fff',
      minHeight: '100vh',
      display: 'flex', flexDirection: 'column',
      padding: isMobile ? '0 0 24px 0' : '48px 56px',
    }}>
      {isMobile && (
        <div style={{
          background: `radial-gradient(ellipse at top right, ${BRAND}, transparent 55%),
                       linear-gradient(180deg, ${BRAND_DARKER} 0%, ${BRAND_DARK} 100%)`,
          padding: '32px 22px 40px 22px',
          color: '#fff',
          borderRadius: '0 0 28px 28px',
          position: 'relative', overflow: 'hidden',
          marginBottom: 20,
          boxShadow: '0 10px 30px rgba(7, 94, 84, 0.4)',
        }}>
          {/* Orbs premium */}
          <div style={{
            position: 'absolute', top: -60, right: -60, width: 200, height: 200,
            borderRadius: '50%', background: BRAND, opacity: 0.35, filter: 'blur(50px)',
          }} />
          <div style={{ position: 'absolute', inset: 0, backgroundImage: GRID_BG, opacity: 0.15 }} />

          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/pwa/icon-192.png" alt="Futezap" width={36} height={36}
              style={{ borderRadius: 10, boxShadow: '0 6px 14px rgba(0,0,0,0.35)' }} />
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: -0.3 }}>Futezap</div>
              <div style={{ fontSize: 9, opacity: 0.7, letterSpacing: 1.5, fontWeight: 600 }}>COMUNIDADE</div>
            </div>
          </div>

          <div style={{ position: 'relative', marginTop: 24 }}>
            <div style={{
              display: 'inline-block', padding: '4px 10px', borderRadius: 100,
              background: 'rgba(37, 211, 102, 0.2)',
              border: '1px solid rgba(37, 211, 102, 0.4)',
              color: '#a3e635', fontSize: 10, fontWeight: 700, marginBottom: 10,
              letterSpacing: 0.5,
            }}>
              <ThunderboltOutlined /> A REDE DA PELADA
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, lineHeight: 1.1, letterSpacing: -0.5 }}>
              Onde boleiros e times<br/>
              <span style={{
                background: `linear-gradient(90deg, ${BRAND}, #a3e635)`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>se encontram!</span>
            </div>
          </div>
        </div>
      )}

      <div style={{
        flex: isMobile ? 'unset' : 1,
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'center',
        padding: isMobile ? '0 20px' : 0,
      }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          {(() => {
            const isOrgFlow = (new URLSearchParams(window.location.search).get('next') || '').startsWith('/time');
            return (
              <>
                <Title level={isMobile ? 3 : 2} style={{ margin: 0, fontWeight: 900, letterSpacing: -0.8, color: INK }}>
                  {isOrgFlow ? 'Vamos cadastrar seu time' : 'Bora jogar bola?'}
                </Title>
                <Text style={{ fontSize: 14, color: '#5a6a63' }}>
                  {isOrgFlow
                    ? 'Antes do time, entra com sua conta. Sem senha.'
                    : 'Entra em 1 clique. Sem senha.'}
                </Text>
              </>
            );
          })()}

          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center' }}>
            <div ref={gBtnRef} style={{ minHeight: 44, width: '100%' }} />
          </div>

          {!process.env.REACT_APP_GOOGLE_CLIENT_ID && (
            <Alert
              type="warning" showIcon
              message="Google não configurado"
              description="Setar REACT_APP_GOOGLE_CLIENT_ID no build."
              style={{ marginTop: 12 }}
            />
          )}

          <div style={{
            position: 'relative', margin: '20px 0 16px 0', textAlign: 'center',
          }}>
            <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 1, background: '#e8e8e8' }} />
            <span style={{
              position: 'relative', background: '#fff', padding: '0 14px',
              fontSize: 12, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 1.2,
            }}>ou entre com WhatsApp</span>
          </div>

          {!showPasswordLogin ? (
            <Button
              block size="large"
              icon={<WhatsAppOutlined />}
              onClick={() => setShowPasswordLogin(true)}
              style={{
                height: 52, borderRadius: 12, borderColor: '#e0e0e0',
                fontWeight: 700, fontSize: 15,
              }}
            >
              Entrar com WhatsApp e senha
            </Button>
          ) : (
            <Form onFinish={handlePasswordLogin} layout="vertical" size="large">
              <Form.Item
                name="phone"
                label="WhatsApp (DDD + número)"
                rules={[
                  { required: true, message: 'WhatsApp é obrigatório' },
                  { pattern: /^\d{10,11}$/, message: 'Só números' },
                ]}
              >
                <Input prefix={<UserOutlined />} placeholder="11987654321" maxLength={11}
                  style={{ height: 48, borderRadius: 10 }} />
              </Form.Item>
              <Form.Item
                name="password" label="Senha"
                rules={[{ required: true, message: 'Senha é obrigatória' }]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Sua senha"
                  style={{ height: 48, borderRadius: 10 }} />
              </Form.Item>
              <Button
                type="primary" htmlType="submit" loading={loading} block
                style={{
                  height: 52, borderRadius: 12, background: BRAND, borderColor: BRAND,
                  fontWeight: 800, fontSize: 15,
                  boxShadow: `0 10px 24px rgba(37, 211, 102, 0.4)`,
                }}
              >
                Entrar
              </Button>
              <Button type="link" block onClick={() => setShowPasswordLogin(false)} style={{ marginTop: 8 }}>
                ← Voltar para Google
              </Button>
            </Form>
          )}

          <Paragraph style={{ textAlign: 'center', marginTop: 24, marginBottom: 0 }}>
            <Text style={{ fontSize: 13, color: '#5a6a63' }}>
              Ainda não tá aqui?{' '}
              <a href={`/player/register${window.location.search}`} style={{ color: BRAND_DARK, fontWeight: 700 }}>
                Fazer cadastro
              </a>
            </Text>
          </Paragraph>

          {/* Card "sou dono de time" no mobile (compact) */}
          {isMobile && (
            <a href="/time" style={{
              display: 'block', marginTop: 24, padding: 16, borderRadius: 14,
              background: `linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)`,
              border: `2px solid ${BRAND_DARK}`, textDecoration: 'none',
              boxShadow: '0 8px 20px rgba(18, 140, 126, 0.15)',
            }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: `linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND_DARKER} 100%)`,
                  color: '#fff', fontSize: 22, display: 'grid', placeItems: 'center',
                  flexShrink: 0,
                }}>👥</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: BRAND_DARKER, letterSpacing: 0.5 }}>PRO DONO DE TIME</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: INK, marginTop: 2 }}>Cadastra teu time</div>
                  <div style={{ fontSize: 12, color: '#5a6a63' }}>Aparece no mapa e recebe boleiros</div>
                </div>
                <div style={{ fontSize: 18, color: BRAND_DARK }}>→</div>
              </div>
            </a>
          )}

          <div style={{ textAlign: 'center', marginTop: 16, fontSize: 11, color: '#bbb', paddingBottom: 12 }}>
            Ao entrar, você aceita nossos{' '}
            <a href="/termos" style={{ color: '#999', textDecoration: 'underline' }}>termos</a>.
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div style={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1.1fr 1fr',
      }}>
        {!isMobile && leftPane}
        {rightPane}
      </div>

      <Modal
        title={null}
        open={regModal.open}
        onCancel={() => setRegModal({ open: false, idToken: '', profile: null })}
        footer={null}
        maskClosable={false}
        width={480}
        styles={{ body: { padding: 32 } }}
      >
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <img src="/pwa/icon-192.png" alt="Futezap" width={60} height={60}
            style={{ borderRadius: 18, marginBottom: 12, boxShadow: '0 10px 24px rgba(0,0,0,0.25)' }} />
          <Title level={3} style={{ marginBottom: 4, marginTop: 0, fontWeight: 800 }}>Falta pouco!</Title>
          <Text type="secondary">Só precisamos do seu WhatsApp pra achar seus times.</Text>
        </div>

        <Alert
          type="info" showIcon
          message="Precisa estar em pelo menos 1 time ativo"
          description="Se o organizador ainda não te adicionou, peça pra ele antes de continuar."
          style={{ marginBottom: 20, borderRadius: 10 }}
        />

        <Form form={regForm} layout="vertical" size="large">
          <Form.Item label="E-mail (do Google)">
            <Input disabled value={regModal.profile?.email || ''} style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item name="name" label="Seu nome"
            rules={[{ required: true, message: 'Informe seu nome' }]}>
            <Input placeholder="Nome completo" style={{ height: 48, borderRadius: 10 }} disabled={regStep === 2} />
          </Form.Item>
          <Form.Item
            name="phone" label="WhatsApp (DDD + número)"
            rules={[
              { required: true, message: 'WhatsApp obrigatório' },
              { pattern: /^\d{10,11}$/, message: 'Apenas números, DDD + telefone' },
            ]}
            extra={regStep === 1 ? 'Exemplo: 11987654321' : `Código enviado para ${phoneOtp}`}
          >
            <Input prefix={<WhatsAppOutlined />} placeholder="11987654321" maxLength={11}
              style={{ height: 48, borderRadius: 10 }} disabled={regStep === 2} />
          </Form.Item>

          {regStep === 1 && (
            <Button
              type="primary" onClick={sendOtp} block loading={sending}
              style={{
                height: 52, borderRadius: 12, background: BRAND, borderColor: BRAND,
                fontWeight: 700, fontSize: 16, boxShadow: `0 8px 20px rgba(37, 211, 102, 0.35)`,
              }}
            >
              Enviar código no WhatsApp
            </Button>
          )}

          {regStep === 2 && (
            <>
              <Form.Item label="Código de 6 dígitos (recebido no WhatsApp)">
                <Input
                  size="large"
                  maxLength={6}
                  autoFocus
                  placeholder="123456"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, ''))}
                  style={{
                    height: 56, borderRadius: 12, fontSize: 22, letterSpacing: 8,
                    textAlign: 'center', fontWeight: 700,
                  }}
                />
              </Form.Item>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Button
                  type="link" size="small"
                  onClick={() => { setRegStep(1); setCodeInput(''); }}
                >← Corrigir número</Button>
                <Button
                  type="link" size="small"
                  disabled={otpCooldownMs > 0 || sending}
                  onClick={sendOtp}
                >
                  {otpCooldownMs > 0 ? `Reenviar em ${humanCooldown(otpCooldownMs)}` : 'Reenviar código'}
                </Button>
              </div>

              <Button
                type="primary" onClick={submitRegister} block loading={regLoading}
                disabled={codeInput.length < 6}
                style={{
                  height: 52, borderRadius: 12, background: BRAND, borderColor: BRAND,
                  fontWeight: 700, fontSize: 16, boxShadow: `0 8px 20px rgba(37, 211, 102, 0.35)`,
                }}
              >
                Concluir cadastro
              </Button>

              <div style={{ textAlign: 'center', marginTop: 10, fontSize: 11, color: '#999' }}>
                O código expira em 10 minutos.
              </div>
            </>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default PlayerLogin;
